import {httpsCallable} from "firebase/functions";
// @ts-ignore -- "FirebaseUtils" is resolved by the web bundler, not by tsc
import {functions} from "FirebaseUtils";
import {PAGE_URLS} from "constants/AppConstants.js";
import LogService from "services/LogService.js";
import {openPaddleCheckout} from "services/PaddleCheckout.js";

export type CheckoutPlan = "monthly" | "yearly";
/** Access level to purchase. "premium" is surfaced as Pro in the UI. */
export type CheckoutTier = "premium" | "ultra";

export interface StartCheckoutResult {
  success: boolean;
  /** The buyer closed the checkout without paying: not an error. */
  cancelled?: boolean;
  error?: string;
}

export interface StartCheckoutOptions {
  /** Adapty paywall variation this purchase should be attributed to */
  variationId?: string;
  /** Which access level to buy. Defaults to premium (Pro). */
  tier?: CheckoutTier;
  /**
   * Adapty placement the user is buying from. The server re-resolves the paywall
   * for it to pick the price, so dashboard changes and A/B tests take effect.
   */
  placementId?: string;
  /** Path (and query) to return to after checkout. Defaults to the onboarding flow. */
  returnPath?: string;
}

/**
 * Sells a new web subscription through Paddle, the merchant of record (it handles
 * VAT and sales tax). The createPaddleCheckout Cloud Function creates the
 * transaction, choosing the price and the first-time-only 14-day trial, and the
 * browser opens Paddle's overlay on it. Adapty picks the subscription up through
 * its Paddle integration.
 *
 * Once paid, the browser goes to the same `?checkout=success` URL a redirect
 * checkout returned to, so callers handle the return as before.
 */
export const startTrialCheckout = async (
  plan: CheckoutPlan,
  options: StartCheckoutOptions = {}
): Promise<StartCheckoutResult> => {
  const {variationId, tier = "premium", placementId, returnPath = PAGE_URLS.onboardingPageUrl} = options;
  // The return path may already carry a query string (a gate opened the paywall
  // on a page with one), so the checkout marker joins it rather than replacing it.
  const successUrl = `${window.location.origin}${returnPath}${returnPath.includes("?") ? "&" : "?"}checkout=success`;
  try {
    const createCheckout = httpsCallable<any, any>(functions, "createPaddleCheckout");
    const result = await createCheckout({plan, tier, variationId, placementId});
    const transactionId = result.data?.transactionId;
    if (!transactionId) {
      return {success: false, error: "No transaction returned"};
    }
    if ((await openPaddleCheckout(transactionId)) === "completed") {
      window.location.assign(successUrl);
      return {success: true};
    }
    return {success: false, cancelled: true};
  } catch (error: any) {
    LogService.addErrorLogAsync("", "Error starting Paddle checkout: " + error.message, false, true);
    return {success: false, error: error.message};
  }
};

export interface PlanPrice {
  /** Smallest currency unit (e.g. 2999 for €29.99). */
  amount: number;
  currency: string;
  interval: string | null;
  priceId: string;
}

/** prices[tier][plan] — e.g. prices.ultra.yearly */
export type PlanPrices = Record<string, Record<string, PlanPrice>>;

/**
 * Shared per placement so prefetchPlanPrices and the paywall use one request.
 * Failures are evicted, so the next caller retries instead of inheriting them.
 */
const priceRequests = new Map<string, Promise<PlanPrices | null>>();

const requestPlanPrices = async (placementId?: string): Promise<PlanPrices | null> => {
  try {
    const callable = httpsCallable<any, any>(functions, "getWebPlanPrices");
    const result = await callable({placementId});
    return result.data?.success ? (result.data.prices as PlanPrices) : null;
  } catch (error: any) {
    LogService.addErrorLogAsync("", "Error fetching plan prices: " + error.message, false, true);
    return null;
  }
};

/**
 * Reads the prices to display from the biller the purchase will go through, so
 * what the user sees is what they get charged.
 * Returns null on any failure — the picker then just omits the prices.
 */
export const fetchPlanPrices = (placementId?: string): Promise<PlanPrices | null> => {
  const key = placementId || "";
  const pending = priceRequests.get(key);
  if (pending) {
    return pending;
  }
  const request = requestPlanPrices(placementId).then((prices) => {
    if (!prices) {
      priceRequests.delete(key);
    }
    return prices;
  });
  priceRequests.set(key, request);
  return request;
};

/** Warms the lookup so its cold start overlaps an earlier step. Needs auth. */
export const prefetchPlanPrices = (placementId?: string): void => {
  void fetchPlanPrices(placementId);
};

/**
 * Formats a plan amount for display in the user's locale.
 */
export const formatPlanPrice = (price: PlanPrice | undefined, locale: string): string | null => {
  if (!price || typeof price.amount !== "number" || !price.currency) {
    return null;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: price.currency.toUpperCase(),
      // Amounts are integers in the minor unit; drop ".00" when it is round.
      minimumFractionDigits: price.amount % 100 === 0 ? 0 : 2
    }).format(price.amount / 100);
  } catch {
    return null;
  }
};

/**
 * Opens Paddle's hosted portal (card, invoices, cancellation) in a new tab, leaving
 * the app where it was. The link is minted per visit and signs them in, so it is
 * opened at once, never kept. Must be called from the click itself: the tab is
 * opened before the network call, or popup blockers would stop it.
 * Returns false when it could not be opened.
 */
export const openBillingPortal = async (): Promise<boolean> => {
  const tab = window.open("about:blank", "_blank");
  if (tab) {
    tab.opener = null;
  }
  try {
    const createSession = httpsCallable<any, any>(functions, "createPaddlePortalSession");
    const result = await createSession({});
    const url = result.data?.url;
    if (typeof url !== "string" || !url.startsWith("https://")) {
      tab?.close();
      return false;
    }
    if (tab) {
      tab.location.href = url;
    } else {
      window.location.assign(url);
    }
    return true;
  } catch (error: any) {
    tab?.close();
    LogService.addErrorLogAsync("", "Error opening the Paddle billing portal: " + error.message, false, true);
    return false;
  }
};

export interface ChangeTierResult extends StartCheckoutResult {
  /** Why the server refused, when it is something to tell the user (e.g. "cheaper-during-trial"). */
  reason?: string;
}

/**
 * Moves an existing web subscription onto another plan/tier (e.g. Pro → Ultra)
 * by swapping the price on the subscription the user already has, on Stripe or
 * Paddle, so they are not billed for two subscriptions at once. The difference is
 * prorated onto the next renewal, so the change applies in place.
 *
 * A Paddle subscription still in its trial can only change by ending the trial and
 * paying now; `endTrial` says the user was shown that.
 */
export const changeSubscriptionTier = async (
  plan: CheckoutPlan,
  tier: CheckoutTier,
  variationId?: string,
  placementId?: string,
  endTrial = false
): Promise<ChangeTierResult> => {
  try {
    const changeTier = httpsCallable<any, any>(functions, "changeWebSubscriptionTier");
    const result = await changeTier({plan, tier, variationId, placementId, endTrial});
    if (result.data?.success) {
      return {success: true};
    }
    return {success: false, error: "Subscription change failed"};
  } catch (error: any) {
    LogService.addErrorLogAsync("", "Error changing web subscription tier: " + error.message, false, true);
    return {success: false, error: error.message, reason: error.details?.reason};
  }
};

export interface PaymentLinkRequest {
  tier: CheckoutTier;
  plan: CheckoutPlan;
  /** Offer the 14-day trial when the buyer is eligible for it. */
  trial: boolean;
  discount?: PaymentLinkDiscount;
  /** Days before the link stops working (30 by default). */
  validDays?: number;
}

/**
 * An existing Paddle discount, or a one-off one: a percentage or a USD amount,
 * covering `payments` payments (1 the first only, 0 every one).
 */
export type PaymentLinkDiscount =
  | {discountId: string}
  | {type: "percentage" | "flat"; amount: number; payments: number};

export interface PaddleDiscount {
  id: string;
  code: string | null;
  description: string;
  type: string;
  amount: string;
  currency: string | null;
  recur: boolean;
  maximumRecurringIntervals: number | null;
  expiresAt: string | null;
  restrictTo: string[];
  timesUsed: number;
  usageLimit: number | null;
}

/** Admin only: archives a Paddle discount (Paddle cannot delete one), ending its code at checkout. */
export const archivePaddleDiscount = async (discountId: string): Promise<void> => {
  const callable = httpsCallable<any, any>(functions, "archivePaddleDiscount");
  await callable({discountId});
};

/** Admin only: the active Paddle discounts, only those fitting `plan` of `tier` when given. */
export const listPaymentLinkDiscounts = async (plan?: {
  tier: CheckoutTier;
  plan: CheckoutPlan;
}): Promise<PaddleDiscount[]> => {
  const callable = httpsCallable<any, any>(functions, "listPaddleDiscounts");
  const {data} = await callable(plan || {});
  return data?.discounts || [];
};

export interface NewPaddleDiscount {
  code: string;
  description?: string;
  type: "percentage" | "flat";
  amount: number;
  /** Payments it covers: 1 the first only, 0 every one. */
  payments: number;
  expiresAt?: string;
  /** Product or price ids the discount is limited to; any product when empty. */
  restrictTo?: string[];
}

export interface PaddleProduct {
  id: string;
  name: string;
  prices: {id: string; label: string}[];
}

/** Admin only: the active products and prices a discount can be limited to. */
export const listDiscountableProducts = async (): Promise<PaddleProduct[]> => {
  const callable = httpsCallable<any, any>(functions, "listPaddleProducts");
  const {data} = await callable({});
  return data?.products || [];
};

/** Admin only: changes a Paddle discount; sends the whole form, like its creation. */
export const updatePaddleDiscount = async (discountId: string, discount: NewPaddleDiscount): Promise<void> => {
  const callable = httpsCallable<any, any>(functions, "updatePaddleDiscount");
  await callable({discountId, ...discount});
};

/** Admin only: creates a Paddle discount customers can enter as a code at checkout. */
export const createPaddleDiscount = async (discount: NewPaddleDiscount): Promise<string> => {
  const callable = httpsCallable<any, any>(functions, "createPaddleDiscount");
  const {data} = await callable(discount);
  return data.id;
};

/**
 * Admin only: a link to send to a customer. It opens the web onboarding on the
 * offer, which has them sign in or up and then pay; the code in it is the offer.
 */
export const createPaymentLink = async (request: PaymentLinkRequest): Promise<string> => {
  const callable = httpsCallable<any, any>(functions, "createPaddlePaymentLink");
  const {data} = await callable(request);
  return `${window.location.origin}${PAGE_URLS.payPageUrl}?${new URLSearchParams({link: data.linkCode})}`;
};

export interface PaymentLinkOffer {
  tier: CheckoutTier;
  plan: CheckoutPlan;
  /** Offered when the account never subscribed before. */
  trial: boolean;
  /** A percentage or a USD amount off, over `payments` payments (1 the first only, 0 every one). */
  discount: {type: "percentage" | "flat"; amount: number; payments: number; code: string | null} | null;
  /** Already used by another account than the signed-in one. */
  used: boolean;
  expired: boolean;
}

/** The offer behind a payment link, readable before signing in; null for an unknown link. */
export const describePaymentLink = async (code: string): Promise<PaymentLinkOffer | null> => {
  try {
    const callable = httpsCallable<any, any>(functions, "describePaddlePaymentLink");
    const {data} = await callable({code});
    return data?.success ? (data as PaymentLinkOffer) : null;
  } catch (error: any) {
    LogService.addErrorLogAsync("", "Error reading a payment link: " + error.message, false, true);
    return null;
  }
};

export interface PaymentLinkCheckout {
  transactionId?: string;
  /** Why it could not open, when it is something to tell the user (e.g. "used"). */
  reason?: string;
  error?: string;
}

/** The transaction a payment link's offer opens for the signed-in account. */
export const startPaymentLinkCheckout = async (code: string): Promise<PaymentLinkCheckout> => {
  try {
    const callable = httpsCallable<any, any>(functions, "startPaddlePaymentLink");
    const {data} = await callable({code});
    return {transactionId: data?.transactionId};
  } catch (error: any) {
    LogService.addErrorLogAsync("", "Error starting a payment link checkout: " + error.message, false, true);
    return {reason: error.details?.reason, error: error.message};
  }
};
